import { exVat } from './revenue'
import { redeemCents } from './ledger'
import type { RevenueOrder } from './revenue'
import type { CloseWarningCode, KassaSummary, KuurStop, Sale, SaleLine, SaleLineInput } from '../types/kassa'
import type { VoucherValidFor } from '../types/vouchers'
import type { Brand } from '../types/catalog'

/**
 * Rekenregels van de kassa (docs/KASSA-ABONNEMENTEN.md §3). Puur en getest; de server rekent
 * hetzelfde. Alles in hele centen.
 */

/** Hoogstens zoveel regels op één bon. */
export const MAX_SALE_LINES = 30
export const MAX_QTY = 99
/** Waarde van een cadeaubon aan de kassa: € 1 tot € 1.000, zoals een handmatige bon. */
export const GIFTCARD_MIN_CENTS = 100
export const GIFTCARD_MAX_CENTS = 100_000

export const lineQty = (l: SaleLineInput) => (l.kind === 'product' ? l.qty : 1)

/** Wat een regel kost: prijs × aantal, of 0 als de behandeling uit een vooruitbetaalde kuur komt. */
export const lineTotalCents = (l: SaleLineInput) => (l.kind === 'treatment' && l.fromKuur ? 0 : l.priceCents * lineQty(l))

export const saleTotalCents = (lines: readonly SaleLineInput[]) => lines.reduce((s, l) => s + lineTotalCents(l), 0)

/** Het deel van de bon dat behandelingen zijn (los of als kuur): daarop mag een bon "alleen behandelingen". */
export const treatmentShareCents = (lines: readonly SaleLineInput[]) =>
  lines.filter((l) => l.kind === 'treatment' || l.kind === 'kuur').reduce((s, l) => s + lineTotalCents(l), 0)

/** Wat er met een cadeaubon betaald mag worden: alles behalve een verkochte cadeaubon. */
export const voucherPayableCents = (lines: readonly SaleLineInput[]) => saleTotalCents(lines.filter((l) => l.kind !== 'giftcard'))

/** Hoeveel er van een cadeaubon af gaat: nooit meer dan het saldo, een behandelbon alleen op behandelingen, nooit op een cadeaubon. */
export const voucherCentsFor = (balanceCents: number, validFor: VoucherValidFor, lines: readonly SaleLineInput[]) =>
  redeemCents(balanceCents, validFor === 'treatments' ? treatmentShareCents(lines) : voucherPayableCents(lines))

/**
 * Hoeveel klanttegoed er maximaal af kan: nooit meer dan het saldo, en alleen op wat er na de
 * cadeaubon nog openstaat van de regels die geen verkochte cadeaubon zijn.
 */
export const creditCentsFor = (balanceCents: number, lines: readonly SaleLineInput[], voucherCents: number) =>
  Math.max(0, Math.min(balanceCents, voucherPayableCents(lines) - voucherCents))

const isDiscounted = (l: SaleLineInput, listPriceCents: number) =>
  l.kind !== 'giftcard' && !(l.kind === 'treatment' && l.fromKuur) && l.priceCents < listPriceCents

export const NO_BRAND_DISCOUNT = 'Dit merk staat geen korting toe.'

/** Mag er aan de kassa korting op producten van dit merk? Standaard niet (onbekend merk ook niet). */
export const brandAllowsDiscount = (brands: readonly Brand[], brandName: string) => brands.some((b) => b.name === brandName && b.discountAllowed === true)

/**
 * Wat er niet klopt aan één regel, of `null`. `discountAllowed: false` bij een product van een merk
 * zonder korting: dan mag de prijs niet onder de catalogusprijs.
 */
export function lineProblem(l: SaleLineInput, listPriceCents: number, opts: { discountAllowed?: boolean } = {}): string | null {
  if (!Number.isInteger(l.priceCents) || l.priceCents < 0) return 'Vul een geldige prijs in.'
  if (l.kind === 'product' && opts.discountAllowed === false && l.priceCents < listPriceCents) return NO_BRAND_DISCOUNT
  if (l.kind === 'giftcard') {
    if (l.priceCents < GIFTCARD_MIN_CENTS || l.priceCents > GIFTCARD_MAX_CENTS) return 'Een cadeaubon is € 1 tot € 1.000.'
    if (l.validFor !== 'all' && l.validFor !== 'treatments') return 'Kies waarvoor de cadeaubon geldt.'
    return null
  }
  if (l.kind === 'product' && (!Number.isInteger(l.qty) || l.qty < 1 || l.qty > MAX_QTY)) return `Aantal tussen 1 en ${MAX_QTY}.`
  if (isDiscounted(l, listPriceCents) && !l.discountReason?.trim()) return 'Lager dan de prijs: geef een reden voor de korting.'
  return null
}

/** Contant: wat de klant gaf moet het te betalen bedrag dekken. */
export function cashProblem(receivedCents: number | undefined, toPayCents: number): string | null {
  if (receivedCents === undefined || !Number.isInteger(receivedCents) || receivedCents < 0) return 'Vul in hoeveel je contant kreeg.'
  if (receivedCents < toPayCents) return 'Dat is minder dan te betalen.'
  return null
}

export const changeCents = (receivedCents: number, toPayCents: number) => Math.max(0, receivedCents - toPayCents)

/** Gesplitst: het contante deel is meer dan 0 en minder dan het te betalen bedrag (anders is het gewoon pin of contant). */
export function splitProblem(cashPartCents: number | undefined, toPayCents: number): string | null {
  if (cashPartCents === undefined || !Number.isInteger(cashPartCents) || cashPartCents <= 0) return 'Vul in hoeveel ze contant betaalt.'
  if (cashPartCents >= toPayCents) return 'Dat is het hele bedrag: kies dan Contant.'
  return null
}

/** Wat er per manier binnenkwam bij één bon: pin, contant (los of het contante deel). */
export function saleMethods(s: Pick<Sale, 'method' | 'methodCents' | 'cashPartCents'>): { pin: number; contant: number } {
  const cash = s.method === 'contant' ? s.methodCents : (s.cashPartCents ?? 0)
  return { pin: s.methodCents - cash, contant: cash }
}

// ——— Nummering ———

/** "S2026-0007": salonbonnen, per jaar doorlopend en los van de webshopnummers (J7). */
export const saleNumber = (year: string, seq: number) => `S${year}-${String(seq).padStart(4, '0')}`

/** Het volgende nummer in een reeks ("S" bonnen, "C" creditnota's) op `day`, per jaar zonder gaten. */
function nextNumber(letter: string, existing: readonly string[], day: string): string {
  const year = day.slice(0, 4)
  const prefix = `${letter}${year}-`
  const max = existing.filter((n) => n.startsWith(prefix)).reduce((m, n) => Math.max(m, Number(n.slice(prefix.length)) || 0), 0)
  return `${prefix}${String(max + 1).padStart(4, '0')}`
}

/** Het volgende nummer voor een bon op `day`, zonder gaten. */
export const nextSaleNumber = (existing: readonly string[], day: string) => nextNumber('S', existing, day)

/** "C2026-0001": creditnota's (gestopte kuren en retouren), één eigen reeks naast de bonnen. */
export const nextCreditNoteNumber = (existing: readonly string[], day: string) => nextNumber('C', existing, day)

// ——— Btw en totalen ———

/** Btw per tarief over regels (bedragen incl. btw), afgerond per tarief. Tarief 0 valt weg. */
export function vatOfLines(lines: readonly Pick<SaleLine, 'vatRate' | 'totalCents'>[]): { vatRate: number; vatCents: number }[] {
  const byRate = new Map<number, number>()
  lines.forEach((l) => byRate.set(l.vatRate, (byRate.get(l.vatRate) ?? 0) + l.totalCents))
  return [...byRate]
    .filter(([rate, gross]) => rate > 0 && gross > 0)
    .map(([vatRate, gross]) => ({ vatRate, vatCents: gross - exVat(gross, vatRate) }))
    .sort((a, b) => b.vatRate - a.vatRate)
}

const sumKind = (sales: readonly Sale[], kind: SaleLine['kind']) =>
  sales.reduce((s, x) => s + x.lines.filter((l) => l.kind === kind).reduce((t, l) => t + l.totalCents, 0), 0)

/**
 * Totalen van een dag. Gecorrigeerde bonnen tellen alleen als aantal mee. Kuren die met pin of
 * contant zijn terugbetaald, gaan van die methode af (en contant dus van wat er in de la hoort);
 * een terugbetaling als tegoed raakt de kassa niet.
 */
type MoneyBack = Pick<KuurStop, 'method' | 'refundCents'>

export function kassaSummary(all: readonly Sale[], refunds: readonly MoneyBack[] = [], returns: readonly MoneyBack[] = []): KassaSummary {
  const sales = all.filter((s) => s.status === 'betaald')
  const by = (m: Sale['method']) => sales.reduce((t, s) => t + saleMethods(s)[m], 0)
  const backOf = (list: readonly MoneyBack[], m: Sale['method']) => list.reduce((t, r) => t + (r.method === m ? r.refundCents : 0), 0)
  const back = (m: Sale['method']) => backOf(refunds, m) + backOf(returns, m)
  return {
    sales: sales.length,
    corrected: all.length - sales.length,
    totalCents: sales.reduce((t, s) => t + s.totalCents, 0),
    byMethod: {
      pin: by('pin') - back('pin'),
      contant: by('contant') - back('contant'),
      cadeaubon: sales.reduce((t, s) => t + s.voucherCents, 0),
      tegoed: sales.reduce((t, s) => t + (s.creditCents ?? 0), 0),
    },
    byKind: { treatments: sumKind(sales, 'treatment'), products: sumKind(sales, 'product'), kuren: sumKind(sales, 'kuur'), giftCards: sumKind(sales, 'giftcard') },
    vat: vatOfLines(sales.flatMap((s) => s.lines)),
    expectedCashCents: by('contant') - back('contant'),
    kuurRefundsCents: backOf(refunds, 'pin') + backOf(refunds, 'contant'),
    returnsCents: backOf(returns, 'pin') + backOf(returns, 'contant'),
    voidRefunds: voidRefundsOf(all),
  }
}

/** Wat er bij het corrigeren van een bon terug moet, per manier: precies wat er binnenkwam. */
export function voidRefundOf(s: Pick<Sale, 'method' | 'methodCents' | 'cashPartCents'>): { pinCents: number; cashCents: number } {
  const m = saleMethods(s)
  return { pinCents: m.pin, cashCents: m.contant }
}

/** Opgeteld over de gecorrigeerde bonnen van een dag (alleen wat als teruggegeven is vastgelegd). */
export function voidRefundsOf(all: readonly Sale[]): { pin: number; contant: number } {
  return all.reduce(
    (t, s) => (s.status === 'gecorrigeerd' && s.correction?.refunded ? { pin: t.pin + s.correction.refunded.pinCents, contant: t.contant + s.correction.refunded.cashCents } : t),
    { pin: 0, contant: 0 },
  )
}

/** Bij een kasverschil hoort een notitie (wat er mis ging); zonder verschil mag hij leeg. */
export function closeProblem(countedCents: number | undefined, expectedCents: number, note: string): string | null {
  if (countedCents === undefined || !Number.isInteger(countedCents) || countedCents < 0) return 'Vul in hoeveel contant er in de la zit.'
  if (countedCents !== expectedCents && !note.trim()) return 'Er is een verschil: schrijf kort op wat er gebeurde.'
  return null
}

// ——— Dagafsluiting: bescherming ———

/** Het woord dat je intikt om een dag definitief af te sluiten. */
export const CLOSE_WORD = 'afsluiten'
export const isCloseWord = (typed: string) => typed.trim().toLowerCase() === CLOSE_WORD
/** Zoveel minuten (of meer) voor sluitingstijd telt als "vroeg afsluiten". */
export const EARLY_CLOSE_MINUTES = 60
/** Zo ver terug zoekt de kassa naar vergeten dagen. */
export const UNCLOSED_LOOKBACK_DAYS = 31

export type CloseWarning = { code: CloseWarningCode; text: string }

const hhmm = (t: string) => {
  const [h, m] = t.split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : Number.NaN
}

/**
 * Wat Sarah moet weten vóór ze een dag afsluit. Afsluiten is definitief (geen bonnen of correcties
 * meer), dus elke waarschuwing moet ze bevestigen; de server rekent hetzelfde en weigert anders.
 * - nog_afrekenen: behandelingen die al begonnen of klaar zijn maar geen bon hebben (op een eerdere
 *   dag: alles wat nooit is afgerekend).
 * - nog_afspraken: alleen vandaag, afspraken die nog moeten beginnen.
 * - vroeg: alleen vandaag, meer dan een uur voor sluitingstijd.
 */
export function closeWarnings(input: {
  toSettle: readonly { time: string; started: boolean; finished: boolean }[]
  isToday: boolean
  /** Minuten sinds middernacht, nu. */
  nowMinutes: number
  /** Sluitingstijd van die dag ("17:00"), of `null` als de salon dicht is. */
  closesAt: string | null
}): CloseWarning[] {
  const out: CloseWarning[] = []
  const due = input.toSettle.filter((a) => !input.isToday || a.started || a.finished)
  const later = input.isToday ? input.toSettle.filter((a) => !a.started && !a.finished) : []
  if (due.length) {
    const what = due.length === 1 ? '1 behandeling' : `${due.length} behandelingen`
    out.push({
      code: 'nog_afrekenen',
      text: input.isToday
        ? `${what} ${due.length === 1 ? 'staat' : 'staan'} nog open om af te rekenen. Na afsluiten kan dat niet meer.`
        : `${what} van deze dag ${due.length === 1 ? 'is' : 'zijn'} nooit afgerekend.`,
    })
  }
  const first = later[0]
  if (first) {
    out.push({
      code: 'nog_afspraken',
      text: `Er ${later.length === 1 ? 'staat' : 'staan'} vandaag nog ${later.length === 1 ? '1 afspraak' : `${later.length} afspraken`}, de eerste om ${first.time}.`,
    })
  }
  if (input.isToday && input.closesAt) {
    const close = hhmm(input.closesAt)
    if (Number.isFinite(close) && input.nowMinutes < close - EARLY_CLOSE_MINUTES) {
      out.push({ code: 'vroeg', text: `De salon is vandaag open tot ${input.closesAt}. Je sluit ruim voor sluitingstijd af.` })
    }
  }
  return out
}

/** Waarschuwingen die (nog) niet bevestigd zijn. */
export const unacknowledged = (warnings: readonly CloseWarning[], acknowledged: readonly CloseWarningCode[] = []) =>
  warnings.filter((w) => !acknowledged.includes(w.code))

/**
 * Eerdere dagen met geld door de kassa die nooit zijn afgesloten, oudste eerst. Een dag zonder
 * bonnen of terugbetalingen hoeft niet afgesloten te worden.
 */
export function unclosedDays(activityDays: Iterable<string>, closedDays: ReadonlySet<string>, before: string, from: string): string[] {
  return [...new Set(activityDays)].filter((d) => d < before && d >= from && !closedDays.has(d)).sort()
}

// ——— Omzet ———

/** Vaste id voor een behandeling in de omzet, naast product-ids en `kuur-…`. */
export const treatmentRevenueId = (slug: string) => `behandeling-${slug}`

export const revenueId = (l: SaleLine) =>
  l.kind === 'product' ? l.productId : l.kind === 'kuur' ? `kuur-${l.treatmentSlug}` : l.kind === 'giftcard' ? 'cadeaubon-kassa' : treatmentRevenueId(l.treatmentSlug)

/**
 * Een bon als omzet (core/rules/revenue.ts), zodat Omzet en de export de salon meetellen.
 * Een gecorrigeerde bon telt niet: corrigeren kan alleen op de dag zelf, dus er is geen omzet die al
 * naar de boekhouder ging. Regels van €0 (uit kuur) vallen weg; de kuur telde bij de verkoop.
 */
export function saleRevenue(s: Sale): RevenueOrder | null {
  if (s.status !== 'betaald') return null
  return {
    paidAt: s.at,
    channel: 'salon',
    paymentMethod: s.method,
    lines: s.lines
      .filter((l) => l.totalCents > 0)
      .map((l) => ({
        productId: revenueId(l),
        name: l.name,
        qty: lineQty(l),
        unitPriceCents: l.priceCents,
        vatRate: l.vatRate,
        // Een verkochte cadeaubon is geen omzet maar een schuld (apart in rapport en export).
        giftCard: l.kind === 'giftcard',
        treatment: l.kind === 'treatment',
        kuur: l.kind === 'kuur',
      })),
    shippingCents: 0,
    shippingVatRate: 0,
    refunds: [],
    voucherCents: s.voucherCents,
    ...(s.creditCents ? { creditCents: s.creditCents } : {}),
    ...(s.method === 'pin' && s.cashPartCents ? { cashPartCents: s.cashPartCents } : {}),
    ...(s.staffId ? { performerId: s.staffId } : {}),
  }
}

/**
 * Een gestopte kuur als omzet: alleen een terugbetaling (negatieve kuuromzet met het btw-tarief van
 * de verkoop) op de dag van stoppen; de verkoop zelf telde al toen de kuur betaald werd. Als tegoed
 * terugbetaald: er ging geen geld de deur uit, het tegoed (een schuld) ging omhoog. Dat staat in
 * "ontvangen" als een negatief bedrag bij Klanttegoed. `null` bij € 0.
 */
export function kuurStopRevenue(stop: KuurStop): RevenueOrder | null {
  if (stop.refundCents <= 0) return null
  return {
    paidAt: stop.at,
    channel: 'salon',
    // Tegoed: geboekt op pin en meteen verschoven naar tegoed (zie `creditCents`), zodat pin netto 0 blijft.
    paymentMethod: stop.method === 'tegoed' ? 'pin' : stop.method,
    refundOnly: true,
    lines: [
      {
        productId: `kuur-${stop.treatmentSlug}`,
        name: `Kuur ${stop.treatmentName}`,
        qty: 1,
        unitPriceCents: stop.refundCents,
        vatRate: stop.vatRate,
        giftCard: false,
        kuur: true,
      },
    ],
    shippingCents: 0,
    shippingVatRate: 0,
    refunds: [{ at: stop.at, amountCents: stop.refundCents }],
    ...(stop.method === 'tegoed' ? { creditCents: -stop.refundCents } : {}),
  }
}
